<template>
  <div
    class="pwd-recovery-container"
    v-loading.fullscreen.lock="display.isLoading"
  >
    <h3 class="centre">Recover Your Password</h3>

    <!-- Step 1 request verification code -->
    <el-form
      v-show="display.step === 1"
      class="verification-code-request-form"
      ref="verificationCodeRequest"
      :model="verificationCodeRequest"
      :rules="rules.verificationCodeRequest"
    >
      <p class="note">
        <strong>Step 1:</strong> enter the mobile number you use to sign in to
        receive a verification code.
      </p>
      <el-form-item prop="phone">
        <el-input
          v-model="verificationCodeRequest.phone"
          placeholder="Phone number"
        ></el-input>
      </el-form-item>
      <div class="centre">
        <el-button
          class="bg-gold black form-btn"
          :disabled="display.sendCodeButton.disabled"
          @click="onClickSendCode"
        >
          {{
            display.sendCodeButton.disabled
              ? `Resend (${display.sendCodeButton.remainingSeconds}s)`
              : "Send Code"
          }}
        </el-button>
      </div>
    </el-form>

    <!-- Step 2 reset password with verification code -->
    <el-form
      v-show="display.step === 2"
      class="password-reset-form"
      ref="passwordReset"
      :model="passwordReset"
      :rules="rules.passwordReset"
    >
      <p class="note">
        <strong>Step 2:</strong> enter your new password along with the
        verification code received from your mobile phone.
      </p>
      <el-form-item prop="newPasswordInput">
        <el-input
          v-model="passwordReset.newPasswordInput"
          type="password"
          placeholder="New Password"
          show-password
        ></el-input>
      </el-form-item>
      <el-form-item prop="newPasswordConfirm">
        <el-input
          v-model="passwordReset.newPasswordConfirm"
          type="password"
          placeholder="Confirm New Password"
          show-password
        ></el-input>
      </el-form-item>
      <el-form-item prop="verificationCode">
        <el-input
          v-model="passwordReset.verificationCode"
          placeholder="Verification Code"
        ></el-input>
      </el-form-item>
      <div class="centre">
        <el-button class="bg-gold black" @click="onClickResetPassword">
          Reset Password
        </el-button>
      </div>
    </el-form>

    <!-- Completed -->
    <div v-show="display.step === 3">
      <p class="note">
        Congratulations! Your password has been reset successfully.
      </p>
      <div class="centre">
        <el-button class="bg-gold black" @click="onClickGoToSignIn">
          Sign In
        </el-button>
      </div>
    </div>

    <div class="centre back-btn" v-if="display.step === 2">
      <el-button class="bg-black gold" @click="onClickBack">Back</el-button>
    </div>
  </div>
</template>

<script>
import logger from "@/utils/logger";
import { sendVerificationCode, resetPassword } from "@/api/customer";

const name = "PasswordRecovery";
const log = logger(name);

export default {
  name,
  data() {
    const validateRetypePassword = (rule, value, cb) => {
      if (value !== this.passwordReset.newPasswordInput) {
        return cb(new Error("Password mismatch."));
      }
      cb();
    };

    return {
      display: {
        isLoading: false,
        step: 1,

        sendCodeButton: {
          disabled: false,
          timerId: null,
          remainingSeconds: 0,
        },
      },

      verificationCodeId: null,
      customerId: null,

      verificationCodeRequest: {
        phone: "",
      },
      passwordReset: {
        verificationCode: "",
        newPasswordInput: "",
        newPasswordConfirm: "",
      },

      rules: {
        verificationCodeRequest: {
          phone: [
            {
              required: true,
              trigger: "blur",
              message: "Please enter the phone number you use to sign in.",
            },
            {
              min: 10,
              max: 10,
              message:
                "Please type a valid Australian mobile number (10 digits).",
              trigger: "blur",
            },
          ],
        },
        passwordReset: {
          verificationCode: [
            {
              required: true,
              trigger: "blur",
              message:
                "Please enter the verification code you receive from your mobile.",
            },
            {
              min: 6,
              max: 6,
              message: "Please type the correct verification code (6 digits).",
              trigger: "blur",
            },
          ],
          newPasswordInput: [
            {
              required: true,
              message: "Please type password.",
              trigger: "blur",
            },
            {
              min: 6,
              message:
                "Password must be longer than 6 alphanumeric characters.",
              trigger: "blur",
            },
          ],
          newPasswordConfirm: [
            {
              required: true,
              message: "Please re-type password.",
              trigger: "blur",
            },
            { validator: validateRetypePassword, trigger: "blur" },
          ],
        },
      },
    };
  },
  methods: {
    onClickSendCode() {
      this.$refs["verificationCodeRequest"].validate(async (valid) => {
        if (!valid) {
          log.warn("Verification code request form invalid");
          return;
        }
        this.display.isLoading = true;
        try {
          const { verificationCodeId, customerId } = await sendVerificationCode(
            this.verificationCodeRequest.phone
          );
          log.info(
            "Verification code ID",
            verificationCodeId,
            ", customer ID",
            customerId
          );
          this.verificationCodeId = verificationCodeId;
          this.customerId = customerId;
          this.display.sendCodeButton.disabled = true;
          this.display.sendCodeButton.remainingSeconds = 60;
          this.display.sendCodeButton.timerId = setInterval(() => {
            if (this.display.sendCodeButton.remainingSeconds <= 0) {
              this.display.sendCodeButton.disabled = false;
              clearInterval(this.display.sendCodeButton.timerId);
              return;
            }
            --this.display.sendCodeButton.remainingSeconds;
          }, 1000);
          ++this.display.step;
          this.$message({
            type: "success",
            message: "Verification code sent",
          });
        } catch (err) {
          log.warn("Error upon sending verification code", err);
          const message =
            (err &&
              err.response &&
              err.response.data &&
              err.response.data.message) ||
            "Failed to send verification code";
          this.$message({
            type: "error",
            message,
          });
        } finally {
          this.display.isLoading = false;
        }
      });
    },
    onClickResetPassword() {
      this.$refs["passwordReset"].validate(async (valid) => {
        if (!valid) {
          log.warn("Password reset form invalid");
          return;
        }
        this.display.isLoading = true;
        try {
          await resetPassword({
            verificationCodeId: this.verificationCodeId,
            verificationCode: this.passwordReset.verificationCode,
            customerId: this.customerId,
            newPassword: this.passwordReset.newPasswordInput,
          });
          ++this.display.step;
        } catch (err) {
          log.warn("Error upon resetting password", err);
          const message =
            (err &&
              err.response &&
              err.response.data &&
              err.response.data.message) ||
            "Failed to reset password";
          this.$message({
            type: "error",
            message,
          });
        } finally {
          this.display.isLoading = false;
        }
      });
    },
    onClickGoToSignIn() {
      this.$router.replace("/?showSignIn=true");
    },

    onClickBack() {
      if (this.display.step === 1) {
        return;
      }
      --this.display.step;
    },
  },
};
</script>

<style scoped>
.pwd-recovery-container {
  padding: 1rem 1.2rem;
}

.pwd-recovery-container .note {
  font-size: 0.9rem;
}

.pwd-recovery-container .el-form >>> input {
  border: 1px solid #c5b899;
  border-radius: 0;
}

.pwd-recovery-container .form-btn.is-disabled {
  background-color: rgba(197, 184, 153, 0.6) !important;
  color: rgba(23, 23, 23, 0.6) !important;
}

.back-btn {
  margin-top: 0.6rem;
}
</style>